import React, { useState, useEffect } from "react";
import api, { extractApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { calendarComponents, calendarRowClass, fmt, HOUR_OPTIONS, blockDisabledDates } from "@/pages/admin/utils/format";


const BlockedSlotsManager = ({ blocked, onChange }) => {
  const [date, setDate] = useState(undefined);
  const [hour, setHour] = useState(10);
  const [fullDay, setFullDay] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const add = async () => {
    if (!date) return toast.error("اختر التاريخ");
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    const dateStr = `${y}-${m}-${d}`;
    setSubmitting(true);
    try {
      if (fullDay) {
        const { data } = await api.post("/admin/blocked-slots/full-day", {
          date_str: dateStr,
          reason: reason || "حظر يوم كامل",
        });
        const skipped = data.skipped_due_to_existing_bookings || 0;
        toast.success(
          skipped > 0
            ? `تم حظر ${data.created} مواعيد. تم تجاوز ${skipped} موعد(مة) محجوز(ة) مسبقًا.`
            : `تم حظر اليوم بالكامل (${data.created} مواعيد)`
        );
      } else {
        const slot_iso = `${dateStr}T${String(hour).padStart(2, "0")}:00:00`;
        await api.post("/admin/blocked-slots", { slot_iso, reason });
        toast.success("تم حظر الموعد");
      }
      setReason("");
      onChange();
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "تعذّر الحظر"));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/blocked-slots/${id}`);
      onChange();
    } catch {
      toast.error("تعذّر الحذف");
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="card-warm p-6">
        <h3 className="font-heading text-xl text-[#222222]">حظر موعد جديد</h3>
        <p className="text-[#5E5C58] text-sm mt-1">
          الأوقات المحظورة لن تظهر للعميلات في تقويم الحجز.
        </p>
        <div className="mt-4 grid sm:grid-cols-2 gap-4 items-start">
          <div
            className="bg-[#F6F4EE] rounded-2xl p-2 inline-block"
            style={{ direction: "ltr" }}
          >
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={blockDisabledDates}
              weekStartsOn={0}
              showOutsideDays={true}
              components={calendarComponents}
              classNames={calendarRowClass}
              dir="ltr"
              data-testid="admin-block-calendar"
            />
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-[#5E5C58]">الساعة</label>
              <select
                data-testid="admin-block-hour"
                value={hour}
                onChange={(e) => setHour(Number(e.target.value))}
                disabled={fullDay}
                className={`w-full mt-1 border border-[#E5E1D8] rounded-xl px-3 py-2 bg-white ${
                  fullDay ? "opacity-40 cursor-not-allowed" : ""
                }`}
              >
                {HOUR_OPTIONS.map((h) => (
                  <option key={h} value={h}>
                    {String(h).padStart(2, "0")}:00
                  </option>
                ))}
              </select>
            </div>
            <label
              data-testid="admin-block-fullday-toggle"
              className="flex items-center gap-2 cursor-pointer select-none rounded-xl border border-[#E5E1D8] bg-white px-3 py-2"
            >
              <input
                type="checkbox"
                checked={fullDay}
                onChange={(e) => setFullDay(e.target.checked)}
                className="w-4 h-4 accent-[#0A4D3C]"
                data-testid="admin-block-fullday-checkbox"
              />
              <span className="text-sm text-[#222222]">حظر يوم كامل</span>
              <span className="text-xs text-[#5E5C58] ms-auto">
                {fullDay ? "(جميع الساعات 10:00 - 17:00)" : ""}
              </span>
            </label>
            <div>
              <label className="text-sm text-[#5E5C58]">السبب (اختياري)</label>
              <Input
                data-testid="admin-block-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="إجازة / اجتماع..."
                className="mt-1"
              />
            </div>
            <Button
              data-testid="admin-block-add-btn"
              onClick={add}
              disabled={submitting}
              className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
            >
              {submitting ? "جاري الحفظ..." : fullDay ? "حظر اليوم بالكامل" : "حظر الموعد"}
            </Button>
          </div>
        </div>
      </div>

      <div className="card-warm p-6">
        <h3 className="font-heading text-xl text-[#222222]">المواعيد المحظورة</h3>
        <div className="mt-4 space-y-2 max-h-[460px] overflow-auto pe-1">
          {blocked.length === 0 && (
            <p className="text-[#5E5C58] text-sm">لا توجد مواعيد محظورة حاليًا.</p>
          )}
          {blocked.map((b) => (
            <div
              key={b.id}
              data-testid={`admin-blocked-row-${b.id}`}
              className="flex items-center justify-between p-3 rounded-xl border border-[#E5E1D8] bg-white"
            >
              <div>
                <div className="text-sm font-medium">{fmt(b.slot_iso)}</div>
                {b.reason && <div className="text-xs text-[#5E5C58] mt-0.5">{b.reason}</div>}
              </div>
              <Button
                data-testid={`admin-blocked-remove-${b.id}`}
                onClick={() => remove(b.id)}
                variant="outline"
                size="icon"
                className="rounded-full"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


// -----------------------------------------------------------------------------
// Daily Capacity Overrides (raise the 4-session cap for specific dates)
// -----------------------------------------------------------------------------
const CapOverridesManager = () => {
  const [items, setItems] = useState([]);
  const [defaultCap, setDefaultCap] = useState(4);
  const [date, setDate] = useState(undefined);
  const [extra, setExtra] = useState(1);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/cap-overrides");
      setItems(data.items || []);
      setDefaultCap(data.default_cap || 4);
    } catch {
      toast.error("تعذّر تحميل التجاوزات");
    }
  };

  useEffect(() => { refresh(); }, []);

  const save = async () => {
    if (!date) return toast.error("اختر التاريخ");
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    setSubmitting(true);
    try {
      await api.post("/admin/cap-overrides", {
        date_str: `${y}-${m}-${d}`,
        extra_capacity: Number(extra),
        reason: reason || null,
      });
      toast.success("تم حفظ التجاوز");
      setReason("");
      refresh();
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "تعذّر الحفظ"));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/cap-overrides/${id}`);
      refresh();
    } catch {
      toast.error("تعذّر الحذف");
    }
  };

  return (
    <div className="card-warm p-6 mt-6" data-testid="admin-cap-overrides-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading text-xl text-[#222222]">تجاوز سعة اليوم</h3>
          <p className="text-[#5E5C58] text-sm mt-1">
            الحد الافتراضي <strong>{defaultCap}</strong> جلسات عادية لكل يوم. يمكنك زيادة الحد لتاريخ بعينه عند الحاجة.
            (لا يؤثر على «جلسة أوتاد فورية»).
          </p>
        </div>
      </div>

      <div className="mt-5 grid lg:grid-cols-2 gap-6 items-start">
        <div className="bg-[#F6F4EE] rounded-2xl p-2 inline-block" style={{ direction: "ltr" }}>
          <Calendar
            mode="single"
            selected={date}
            onSelect={setDate}
            disabled={blockDisabledDates}
            weekStartsOn={0}
            showOutsideDays={true}
            components={calendarComponents}
            classNames={calendarRowClass}
            dir="ltr"
            data-testid="admin-cap-calendar"
          />
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-sm text-[#5E5C58]">عدد الجلسات الإضافية</label>
            <Input
              type="number" min={1} max={20}
              value={extra}
              onChange={(e) => setExtra(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
              className="mt-1"
              data-testid="admin-cap-extra-input"
            />
            <p className="text-xs text-[#5E5C58] mt-1">
              السعة الفعلية لهذا التاريخ ستصبح: <strong>{defaultCap + Number(extra || 0)}</strong>.
            </p>
          </div>
          <div>
            <label className="text-sm text-[#5E5C58]">السبب (اختياري)</label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="ضغط مواعيد قبل العيد..."
              className="mt-1"
              data-testid="admin-cap-reason-input"
            />
          </div>
          <Button
            onClick={save}
            disabled={submitting}
            className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
            data-testid="admin-cap-save-btn"
          >
            {submitting ? "جاري الحفظ..." : "حفظ التجاوز"}
          </Button>
        </div>
      </div>

      <div className="mt-6">
        <h4 className="font-heading text-base text-[#222222] mb-3">التجاوزات المحفوظة</h4>
        <div className="space-y-2 max-h-[260px] overflow-auto pe-1" data-testid="admin-cap-list">
          {items.length === 0 && (
            <p className="text-[#5E5C58] text-sm">لا توجد تجاوزات حاليًا.</p>
          )}
          {items.map((it) => (
            <div
              key={it.id}
              data-testid={`admin-cap-row-${it.id}`}
              className="flex items-center justify-between p-3 rounded-xl border border-[#E5E1D8] bg-white"
            >
              <div>
                <div className="text-sm font-medium" dir="ltr">{it.date_str}</div>
                <div className="text-xs text-[#5E5C58] mt-0.5">
                  +{it.extra_capacity} جلسات{it.reason ? ` · ${it.reason}` : ""}
                </div>
              </div>
              <Button
                onClick={() => remove(it.id)}
                variant="outline" size="icon"
                className="rounded-full"
                data-testid={`admin-cap-remove-${it.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};



// -----------------------------------------------------------------------------
// Service-specific Blocked Dates manager (Iteration 73)
// Admin can mark a whole DATE as unavailable for ONE service. Backend storage
// is `db.blocked_dates` keyed by (service_id, date); unique index prevents
// duplicates. Hardcoded entries (e.g. SCHEDULED_CALL_BLOCKED_DATES) are
// surfaced here as read-only rows so they remain visible to the admin.
// -----------------------------------------------------------------------------
const serviceBlockedDateDisabled = (d) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (d < today) return true;
  return false;
};


const ServiceBlockedDatesManager = () => {
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [items, setItems] = useState([]);
  const [date, setDate] = useState(undefined);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  // Iteration 74 — "محجوزة بالكامل" status per service, stored in the same
  // `service_visibility` collection. Confirm dialog gates the mutation.
  const [fullyBooked, setFullyBooked] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingValue, setPendingValue] = useState(false);

  // Load services once (consultations only — same shape as the rest of admin).
  // Iteration 74 — /admin/services is preferred over the public /services
  // because it returns each service with its `is_fully_booked` flag.
  useEffect(() => {
    api
      .get("/admin/services")
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : data.services || [];
        const consults = list.filter((s) => s && s.type === "consultation");
        setServices(consults);
        if (consults.length && !serviceId) {
          setServiceId(consults[0].id);
        }
      })
      .catch(() => toast.error("تعذّر تحميل الخدمات"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync the "fully booked" toggle with the currently selected service.
  useEffect(() => {
    const svc = services.find((s) => s.id === serviceId);
    setFullyBooked(!!(svc && svc.is_fully_booked));
  }, [serviceId, services]);

  const refresh = async (sid = serviceId) => {
    if (!sid) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/blocked-dates`, { params: { service_id: sid } });
      // Sort: real entries first (newest first), hardcoded last.
      const sorted = [...(data.items || [])].sort((a, b) => {
        if (!!a.is_hardcoded !== !!b.is_hardcoded) return a.is_hardcoded ? 1 : -1;
        return (a.date || "").localeCompare(b.date || "");
      });
      setItems(sorted);
    } catch {
      toast.error("تعذر تحديث التواريخ المحجوبة، حاول مرة أخرى");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh(serviceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  const add = async () => {
    if (!serviceId) return toast.error("اختر الخدمة");
    if (!date) return toast.error("اختر التاريخ");
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    const dateStr = `${y}-${m}-${d}`;
    setSubmitting(true);
    try {
      await api.post("/admin/blocked-dates", {
        service_id: serviceId,
        date: dateStr,
        reason: reason.trim() || "محجوب من الأدمن",
      });
      toast.success("تم حجب التاريخ لهذه الخدمة");
      setDate(undefined);
      setReason("");
      await refresh();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        toast.error("هذا التاريخ محجوب مسبقًا لهذه الخدمة");
      } else {
        toast.error(
          extractApiErrorMessage(e, "تعذر تحديث التواريخ المحجوبة، حاول مرة أخرى")
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id, isHardcoded) => {
    if (isHardcoded) {
      toast.error("هذا التاريخ مُثبّت في الكود ولا يمكن حذفه من هنا");
      return;
    }
    try {
      await api.delete(`/admin/blocked-dates/${id}`);
      toast.success("تم إتاحة التاريخ لهذه الخدمة");
      await refresh();
    } catch {
      toast.error("تعذر تحديث التواريخ المحجوبة، حاول مرة أخرى");
    }
  };

  // Iteration 74 — fully-booked toggle handlers.
  const requestToggleFullyBooked = (nextValue) => {
    if (!serviceId) return;
    setPendingValue(nextValue);
    setConfirmOpen(true);
  };

  const confirmToggleFullyBooked = async () => {
    setConfirmOpen(false);
    if (!serviceId) return;
    setSavingStatus(true);
    try {
      await api.patch(`/admin/services/${serviceId}/fully-booked`, {
        is_fully_booked: pendingValue,
      });
      // Reflect the new state locally + refresh the services list so the
      // toggle persists after a page reload / re-mount.
      setFullyBooked(pendingValue);
      setServices((prev) =>
        prev.map((s) => (s.id === serviceId ? { ...s, is_fully_booked: pendingValue } : s))
      );
      toast.success(
        pendingValue
          ? "تم تحديد الجلسة كمحجوزة بالكامل"
          : "تمت إعادة إتاحة الجلسة"
      );
    } catch {
      toast.error("تعذر تحديث حالة الجلسة، حاول مرة أخرى");
    } finally {
      setSavingStatus(false);
    }
  };

  return (
    <div className="card-warm p-6 mt-6" data-testid="admin-svc-blocked-dates-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading text-xl text-[#222222]">إدارة التواريخ المحجوبة</h3>
          <p className="text-[#5E5C58] text-sm mt-1">
            احجبي تاريخًا كاملًا لخدمة محددة. هذا الحجب يُطبَّق فقط على الخدمة المختارة ولا يؤثر على باقي الخدمات.
            قواعد الجمعة/السبت ومدة الانتظار تبقى كما هي.
          </p>
        </div>
      </div>

      <div className="mt-5 grid lg:grid-cols-2 gap-6 items-start">
        <div className="space-y-4">
          <div>
            <label className="text-sm text-[#5E5C58]">الخدمة</label>
            <select
              data-testid="admin-svc-blocked-service"
              value={serviceId}
              onChange={(e) => setServiceId(e.target.value)}
              className="w-full mt-1 border border-[#E5E1D8] rounded-xl px-3 py-2 bg-white"
            >
              {services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title_ar}
                </option>
              ))}
            </select>
          </div>

          {/* Iteration 74 — "حالة الجلسة" (fully-booked) toggle for the
              selected service. Persists in the same service_visibility
              collection used by the visibility toggle. */}
          <div
            className="border border-[#E5E1D8] rounded-2xl p-4 bg-white"
            data-testid="admin-svc-fully-booked-card"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-[#222222]">حالة الجلسة</div>
                <div className="text-xs text-[#5E5C58] mt-1 leading-relaxed">
                  عند تفعيل «محجوزة بالكامل» تظهر الجلسة للعملاء بأنها محجوزة
                  ولا يمكنهم الحجز عبر التقويم أو الرابط المباشر. الحجوزات
                  الحالية والتذكيرات لا تتأثر.
                </div>
                <div className="mt-2 text-xs">
                  <span className="text-[#5E5C58]">الحالة الحالية: </span>
                  <span
                    data-testid="admin-svc-fully-booked-status"
                    className={fullyBooked ? "text-[#B33939] font-medium" : "text-[#0A4D3C] font-medium"}
                  >
                    {fullyBooked ? "محجوزة بالكامل" : "متاحة للحجز"}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-sm text-[#5E5C58]">محجوزة بالكامل</span>
                <Switch
                  data-testid="admin-svc-fully-booked-toggle"
                  checked={fullyBooked}
                  disabled={!serviceId || savingStatus}
                  onCheckedChange={(v) => requestToggleFullyBooked(!!v)}
                />
              </div>
            </div>
          </div>

          <div className="bg-[#F6F4EE] rounded-2xl p-2 inline-block" style={{ direction: "ltr" }}>
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={serviceBlockedDateDisabled}
              weekStartsOn={0}
              showOutsideDays={true}
              components={calendarComponents}
              classNames={calendarRowClass}
              dir="ltr"
              data-testid="admin-svc-blocked-calendar"
            />
          </div>

          <div>
            <label className="text-sm text-[#5E5C58]">السبب (اختياري)</label>
            <Input
              data-testid="admin-svc-blocked-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="إجازة / سفر..."
              className="mt-1"
            />
          </div>

          <Button
            data-testid="admin-svc-blocked-add-btn"
            onClick={add}
            disabled={submitting || !serviceId}
            className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
          >
            {submitting ? "جاري الحفظ..." : "إضافة تاريخ محجوب"}
          </Button>
        </div>

        <div>
          <h4 className="font-heading text-base text-[#222222] mb-3">
            التواريخ المحجوبة لهذه الخدمة
          </h4>
          <div
            className="space-y-2 max-h-[460px] overflow-auto pe-1"
            data-testid="admin-svc-blocked-list"
          >
            {loading && (
              <p className="text-[#5E5C58] text-sm">جاري التحميل...</p>
            )}
            {!loading && items.length === 0 && (
              <p className="text-[#5E5C58] text-sm">لا توجد تواريخ محجوبة لهذه الخدمة.</p>
            )}
            {items.map((it) => (
              <div
                key={it.id}
                data-testid={`admin-svc-blocked-row-${it.id}`}
                className={`flex items-center justify-between p-3 rounded-xl border bg-white ${
                  it.is_hardcoded
                    ? "border-[#E5E1D8] opacity-70"
                    : "border-[#E5E1D8]"
                }`}
              >
                <div>
                  <div className="text-sm font-medium" dir="ltr">{it.date}</div>
                  <div className="text-xs text-[#5E5C58] mt-0.5">
                    {it.reason || "محجوب من الأدمن"}
                  </div>
                </div>
                {it.is_hardcoded ? (
                  <Badge
                    variant="outline"
                    className="rounded-full text-xs"
                    data-testid={`admin-svc-blocked-hardcoded-${it.id}`}
                  >
                    مُثبّت
                  </Badge>
                ) : (
                  <Button
                    data-testid={`admin-svc-blocked-remove-${it.id}`}
                    onClick={() => remove(it.id, false)}
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                  >
                    إزالة
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Iteration 74 — confirmation dialog for the fully-booked toggle. */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          dir="rtl"
          className="text-right"
          data-testid="admin-svc-fully-booked-confirm"
        >
          <DialogHeader>
            <DialogTitle className="text-right">
              {pendingValue ? "تأكيد الحجب الكامل" : "تأكيد إعادة الإتاحة"}
            </DialogTitle>
            <DialogDescription className="text-right leading-relaxed pt-1">
              {pendingValue
                ? "سيتم إيقاف الحجز لهذه الجلسة وإظهارها للعملاء بأنها محجوزة بالكامل. هل تريدين المتابعة؟"
                : "سيتم إعادة إتاحة هذه الجلسة حسب التقويم وقواعد التوفر الحالية. هل تريدين المتابعة؟"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-start">
            <Button
              data-testid="admin-svc-fully-booked-confirm-btn"
              onClick={confirmToggleFullyBooked}
              disabled={savingStatus}
              className={
                pendingValue
                  ? "rounded-full bg-[#B33939] hover:bg-[#8f2a2a] text-white"
                  : "rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
              }
            >
              متابعة
            </Button>
            <Button
              data-testid="admin-svc-fully-booked-cancel-btn"
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              className="rounded-full"
            >
              إلغاء
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { BlockedSlotsManager, CapOverridesManager, ServiceBlockedDatesManager };
